import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TenantThemeConfig } from '../types/theme'
import { getActiveLiveTvChannels, huayYoutubeLiveToChannels, type LiveTvChannel } from '../lib/liveTv'
import { parseYoutubeVideoId, toYoutubeEmbedUrl, toYoutubeThumbnailUrl } from '../lib/youtubeUrl'
import { lotteryService } from '../services/lotteryService'

interface ModernLiveTvPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: {
    CDN_BASE_URL: string
    CSS_PATH: string
    IMG_PATH: string
    FONTS_PATH: string
  }
}

type FilterKey = 'all' | 'football' | 'lottery' | 'other'

const ModernLiveTvPage: React.FC<ModernLiveTvPageProps> = ({ themeConfig }) => {
  const navigate = useNavigate()
  const [huayNameByKey, setHuayNameByKey] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const map: Record<string, string> = {}

      // Catalog ทั้งหมดจาก shared resources (ครอบคลุมหวยที่ไม่มีรอบวันนี้ด้วย)
      try {
        const sharedMap = await lotteryService.getSharedHuayNameMap()
        if (cancelled) return
        for (const [k, v] of Object.entries(sharedMap)) {
          if (v && !map[k]) map[k] = v
        }
      } catch {
        // ignore
      }

      // เสริมด้วยรอบวันนี้ (กรณี shared catalog cache ค้างหรือ API ล่ม)
      try {
        const rounds = await lotteryService.getTodayRounds()
        if (cancelled) return
        for (const round of rounds) {
          const name = (round as { huayName?: string }).huayName?.trim()
          if (!name) continue
          const code = (round as { huayCode?: string }).huayCode
          const huayId = (round as { huayId?: string }).huayId
          const id = (round as { _id?: string })._id
          if (code) {
            const k = String(code).trim().toUpperCase()
            if (k && !map[k]) map[k] = name
          }
          if (huayId) {
            const k = String(huayId).trim()
            if (k && !map[k]) map[k] = name
          }
          if (id) {
            const k = String(id).trim()
            if (k && !map[k]) map[k] = name
          }
        }
      } catch {
        // ignore — fallback to label / formatted key
      }

      if (!cancelled) setHuayNameByKey(map)
    })()
    return () => { cancelled = true }
  }, [])

  const channels = useMemo<LiveTvChannel[]>(() => {
    if (themeConfig.settings.liveTvEnabled !== true) return []
    const football = getActiveLiveTvChannels(themeConfig.settings.liveTvChannels || [])
    const lottery = huayYoutubeLiveToChannels(themeConfig.settings.huayYoutubeLive || null, huayNameByKey)
    return [...football, ...lottery]
  }, [themeConfig.settings.liveTvEnabled, themeConfig.settings.liveTvChannels, themeConfig.settings.huayYoutubeLive, huayNameByKey])

  const [filter, setFilter] = useState<FilterKey>('all')
  const [selectedId, setSelectedId] = useState<string | null>(channels[0]?.id || null)

  useEffect(() => {
    if (!channels.length) {
      setSelectedId(null)
      return
    }
    setSelectedId((prev) => {
      if (prev && channels.some((c) => c.id === prev)) return prev
      return channels[0]?.id ?? null
    })
  }, [channels])

  const counts = useMemo(() => {
    const out = { football: 0, lottery: 0, other: 0 }
    for (const c of channels) {
      const cat = c.category ?? 'other'
      if (cat === 'football') out.football += 1
      else if (cat === 'lottery') out.lottery += 1
      else out.other += 1
    }
    return out
  }, [channels])

  const filtered = useMemo(() => {
    if (filter === 'all') return channels
    return channels.filter((c) => (c.category ?? 'other') === filter)
  }, [channels, filter])

  const playingChannel = useMemo(() => {
    if (!selectedId) return channels[0] ?? null
    return channels.find((c) => c.id === selectedId) ?? channels[0] ?? null
  }, [channels, selectedId])

  const videoId = playingChannel ? parseYoutubeVideoId(playingChannel.youtubeUrl) : null
  const playerStartMuted = themeConfig.settings.liveTvPlayerStartMuted !== false

  const renderChip = (key: FilterKey, label: string, count: number) => {
    if (key !== 'all' && count === 0) return null
    const active = filter === key
    return (
      <button
        key={key}
        type="button"
        onClick={() => setFilter(key)}
        className="border-0"
        style={{
          padding: '6px 14px',
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 700,
          color: active ? '#fff' : '#cbd5e1',
          background: active ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)' : 'rgba(255,255,255,0.08)',
          boxShadow: active ? '0 6px 14px rgba(239,68,68,0.35)' : 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {label}
        <span
          style={{
            background: active ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.10)',
            padding: '1px 7px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 800,
          }}
        >
          {count}
        </span>
      </button>
    )
  }

  return (
    <div className="content">
      <div className="container py-3 py-md-4">
        <style>{`
          @keyframes liveTvModernPulse {
            0% { box-shadow: 0 0 0 0 rgba(239,68,68,0.7); }
            70% { box-shadow: 0 0 0 10px rgba(239,68,68,0); }
            100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
          }
          .live-tv-modern-hero {
            position: relative;
            overflow: hidden;
            border-radius: 16px;
            background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
            color: #fff;
            padding: 18px 20px;
            box-shadow: 0 18px 40px rgba(15,23,42,0.30);
          }
          .live-tv-modern-hero::after {
            content: '';
            position: absolute;
            inset: 0;
            background:
              radial-gradient(700px 200px at 110% -10%, rgba(239,68,68,0.45), transparent 60%),
              radial-gradient(500px 180px at -10% 120%, rgba(59,130,246,0.30), transparent 60%);
            pointer-events: none;
          }
          .live-tv-modern-hero > * { position: relative; z-index: 1; }
          .live-tv-modern-card {
            position: relative;
            width: 100%;
            padding: 0;
            background: #0f172a;
            border: 1px solid rgba(148,163,184,0.18);
            border-radius: 12px;
            overflow: hidden;
            cursor: pointer;
            transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
            color: #f8fafc;
          }
          .live-tv-modern-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 14px 30px rgba(15,23,42,0.45);
          }
          .live-tv-modern-card.is-active {
            border-color: #ef4444;
            box-shadow: 0 14px 30px rgba(239,68,68,0.40);
          }
          .live-tv-modern-thumb {
            position: relative;
            width: 100%;
            padding-bottom: 56.25%;
            background: #020617;
          }
          .live-tv-modern-thumb img {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .live-tv-modern-thumb::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(180deg, rgba(2,6,23,0) 55%, rgba(2,6,23,0.85) 100%);
            pointer-events: none;
          }
          .live-tv-modern-pill {
            position: absolute;
            top: 8px;
            right: 8px;
            background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
            color: #fff;
            padding: 3px 8px;
            border-radius: 999px;
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 0.4px;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            z-index: 2;
          }
          .live-tv-modern-pill::before {
            content: '';
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #fff;
            animation: liveTvModernPulse 1.4s infinite;
          }
          .live-tv-modern-now {
            position: absolute;
            top: 8px;
            left: 8px;
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            color: #0f172a;
            padding: 3px 8px;
            border-radius: 999px;
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 0.4px;
            z-index: 2;
          }
          .live-tv-modern-thumb-fallback {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            color: rgba(255,255,255,0.45);
            font-size: 28px;
          }
          .live-tv-modern-meta {
            padding: 10px 12px;
            display: flex;
            align-items: center;
            gap: 8px;
            background: linear-gradient(180deg, #0f172a 0%, #0b1224 100%);
          }
          .live-tv-modern-cat-icon {
            width: 26px;
            height: 26px;
            border-radius: 8px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            font-size: 12px;
          }
          .live-tv-modern-title {
            font-size: 13px;
            font-weight: 700;
            line-height: 1.25;
            margin: 0;
            color: #f8fafc;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
          .live-tv-modern-player {
            background: #000;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 26px 60px rgba(15,23,42,0.45);
          }
        `}</style>

        <div className="live-tv-modern-hero mb-3 d-flex align-items-start justify-content-between flex-wrap gap-2">
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                  color: '#fff',
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: 0.6,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: '#fff',
                    animation: 'liveTvModernPulse 1.4s infinite',
                  }}
                />
                LIVE
              </span>
              <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 600 }}>
                ทั้งหมด {channels.length} ช่อง
              </span>
            </div>
            <h2 style={{ fontWeight: 800, margin: 0, fontSize: 'clamp(20px, 3vw, 26px)', color: '#fff' }}>
              ดูถ่ายทอดสด
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: '4px 0 0', fontSize: 13 }}>
              ช่องบอล / กีฬา / ถ่ายทอดสดหวย — อัปเดตโดยแอดมิน
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="border-0"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.12)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <i className="fas fa-arrow-left" style={{ fontSize: 11 }} />
            กลับหน้าหลัก
          </button>
        </div>

        {channels.length === 0 ? (
          <div
            className="text-center"
            style={{
              padding: 56,
              background: 'rgba(15,23,42,0.55)',
              borderRadius: 16,
              border: '1px solid rgba(148,163,184,0.18)',
              color: '#cbd5e1',
            }}
          >
            <i className="fas fa-tv mb-3" style={{ fontSize: 36, color: '#94a3b8' }} />
            <p className="mb-1" style={{ fontWeight: 600 }}>ยังไม่มีช่องถ่ายทอดสดในขณะนี้</p>
            <small style={{ color: 'rgba(203,213,225,0.7)' }}>
              แอดมินยังไม่ได้เปิดช่องถ่ายทอดสด — กรุณาตรวจสอบในภายหลัง
            </small>
          </div>
        ) : (
          <>
            {playingChannel && videoId && (
              <div className="live-tv-modern-player mb-3">
                <div
                  className="d-flex align-items-center justify-content-between flex-wrap gap-2"
                  style={{
                    padding: '12px 16px',
                    background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 100%)',
                    color: '#fff',
                  }}
                >
                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: 0.5,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: '#fff',
                          animation: 'liveTvModernPulse 1.4s infinite',
                        }}
                      />
                      LIVE
                    </span>
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {playingChannel.title}
                    </span>
                    {playingChannel.category === 'lottery' && (
                      <span
                        className="d-none d-sm-inline-flex"
                        style={{
                          alignItems: 'center',
                          gap: 4,
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: 'rgba(245,158,11,0.18)',
                          color: '#fcd34d',
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        <i className="fas fa-trophy" style={{ fontSize: 10 }} />
                        ถ่ายทอดสดหวย
                      </span>
                    )}
                    {playingChannel.category === 'football' && (
                      <span
                        className="d-none d-sm-inline-flex"
                        style={{
                          alignItems: 'center',
                          gap: 4,
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: 'rgba(59,130,246,0.18)',
                          color: '#93c5fd',
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        <i className="fas fa-futbol" style={{ fontSize: 10 }} />
                        บอล / กีฬา
                      </span>
                    )}
                  </div>
                </div>
                <div className="ratio ratio-16x9" style={{ background: '#000' }}>
                  <iframe
                    key={`${playingChannel.id}-${playerStartMuted ? 'muted' : 'sound'}`}
                    title={playingChannel.title}
                    src={toYoutubeEmbedUrl(videoId, { autoplay: true, muted: playerStartMuted })}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{ border: 0 }}
                  />
                </div>
              </div>
            )}

            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
              {renderChip('all', 'ทั้งหมด', channels.length)}
              {renderChip('football', 'บอล / กีฬา', counts.football)}
              {renderChip('lottery', 'ถ่ายทอดสดหวย', counts.lottery)}
              {renderChip('other', 'อื่นๆ', counts.other)}
            </div>

            <div className="row g-2 g-md-3">
              {filtered.map((channel) => {
                const cVid = parseYoutubeVideoId(channel.youtubeUrl)
                const isActive = selectedId === channel.id
                const isLottery = channel.category === 'lottery'
                const isFootball = channel.category === 'football'
                const catBg = isLottery
                  ? 'linear-gradient(135deg, #fde68a, #f59e0b)'
                  : isFootball
                    ? 'linear-gradient(135deg, #bfdbfe, #3b82f6)'
                    : 'linear-gradient(135deg, #e2e8f0, #94a3b8)'
                const catColor = isLottery ? '#92400e' : isFootball ? '#1e3a8a' : '#334155'
                return (
                  <div key={channel.id} className="col-6 col-md-4 col-lg-3">
                    <button
                      type="button"
                      onClick={() => setSelectedId(channel.id)}
                      className={`live-tv-modern-card ${isActive ? 'is-active' : ''}`}
                    >
                      <div className="live-tv-modern-thumb">
                        {cVid ? (
                          <img
                            src={toYoutubeThumbnailUrl(cVid, 'mq')}
                            alt={channel.title}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        ) : (
                          <div className="live-tv-modern-thumb-fallback">
                            <i className="fas fa-tv" />
                          </div>
                        )}
                        {isActive && <span className="live-tv-modern-now">กำลังเล่น</span>}
                        <span className="live-tv-modern-pill">LIVE</span>
                      </div>
                      <div className="live-tv-modern-meta">
                        <span
                          className="live-tv-modern-cat-icon"
                          style={{ background: catBg, color: catColor }}
                        >
                          <i className={`fas ${isLottery ? 'fa-trophy' : isFootball ? 'fa-futbol' : 'fa-tv'}`} />
                        </span>
                        <h6 className="live-tv-modern-title" title={channel.title}>
                          {channel.title}
                        </h6>
                      </div>
                    </button>
                  </div>
                )
              })}
              {filtered.length === 0 && (
                <div className="col-12 text-center py-4" style={{ color: 'rgba(203,213,225,0.7)' }}>
                  <i className="fas fa-filter me-2" />
                  ไม่มีช่องในหมวดนี้ — ลองเลือกหมวดอื่น
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ModernLiveTvPage
